import { CreateUserInfo } from '@/app/api/user/create/route';
import { CACHE_TIME } from '@/const/cache';
import { RawEPCCCartResponse, RawEPCCPromotion } from '@/types/Cart';
import { EPCCAddress, PRIVEE_STATUS, User } from '@/types/User';

export const US_STATES = [
  '',
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC'
];

export type PreviewCartRequest = {
  insuranceProductId?: string;
  subscriptionProductIds: string[];
  promotionCodes: string[];
};

export type PreviewCartResponse = {
  cart: RawEPCCCartResponse;
  stripeCouponId?: string;
  requiresAuthorizationCharge?: boolean;
  /** The code is used up, so the signup can say so instead of "Applied after acceptance". */
  codeRedeemed?: boolean;
  /** Promo's cancellation-minimum waiver months, for signup consent copy (VIVE-3887). */
  cancellationMinimumWaiverMonths?: number | null;
};

/**
 * Creates a new user at EPCC
 * @param UserInfo
 * @returns
 */
export async function createUser(user: CreateUserInfo): Promise<any> {
  try {
    const response = await fetch('/api/user/create', {
      method: 'POST',
      body: JSON.stringify({
        ...user,
        dodgeballSourceToken: user?.dodgeballSourceToken
      }),
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      }
    });

    const { body } = await response.json();

    return body;
  } catch (error) {
    throw error;
  }
}

/**
 * Verifies if the email already exists at EPCC
 * @param email
 * @returns
 */
export async function doesEmailExist(
  email: string
): Promise<{ userExists: boolean; status?: string }> {
  try {
    const response = await fetch('/api/email', {
      method: 'POST',
      body: JSON.stringify({ email: email.toLowerCase() }),
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      }
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}

/**
 * Update customer's membership with given Stripe plan IDs.
 * If update fails or is invalid, returns false or throws error.
 * Else returns true.
 *
 * @param subscriptionPlanIds
 * @param insurancePlanId
 * @returns boolean
 */
export async function updateMembership(
  subscriptionPlanIds: string[],
  insurancePlanId: string | undefined,
  priveeAction?: 'cancel'
): Promise<boolean> {
  const { success } = await requestMembershipUpdate(
    subscriptionPlanIds,
    insurancePlanId,
    priveeAction
  );
  return success;
}

/**
 * Apply a new applicant's selected plans.
 * Returns the EP subscription order ID, or null if the update failed.
 *
 * @param subscriptionPlanIds
 * @param insurancePlanId
 * @returns order ID
 */
export async function applySignupMembership(
  subscriptionPlanIds: string[],
  insurancePlanId: string | undefined
): Promise<string | null> {
  const { success, orderId } = await requestMembershipUpdate(
    subscriptionPlanIds,
    insurancePlanId
  );
  return success && orderId ? orderId : null;
}

async function requestMembershipUpdate(
  subscriptionPlanIds: string[],
  insurancePlanId: string | undefined,
  priveeAction?: 'cancel'
): Promise<{ success: boolean; orderId?: string }> {
  try {
    const membershipUpdateResponse = await fetch('/api/user/membership', {
      method: 'PUT',
      keepalive: true,
      body: JSON.stringify({
        plans: [
          ...subscriptionPlanIds,
          ...(insurancePlanId ? [insurancePlanId] : [])
        ],
        ...(priveeAction && { priveeAction })
      }),
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      },
      headers: {
        'content-type': 'application/json'
      }
    });

    return await membershipUpdateResponse.json();
  } catch (error) {
    throw error;
  }
}

/**
 * Freeze a Réservé membership.
 * @param priveeFrozenPlanId - the privee-freeze Stripe plan ID from closet subscription options
 */
export async function freezePriveeMembership(
  priveeFrozenPlanId: string
): Promise<boolean> {
  return updateMembership([priveeFrozenPlanId], undefined);
}

/**
 * Create a preview subscription cart, for the purposes of pre-calculating user's membership payments.
 *
 * @param subscriptionProductIds array of subscription product IDs
 * @param promotionCodes array of promo codes
 * @returns
 */
export const createPreviewSubscriptionCart = async (
  previewCart: PreviewCartRequest,
  signal?: AbortSignal
): Promise<PreviewCartResponse> => {
  const response = await fetch(`/api/cart/preview`, {
    next: {
      revalidate: CACHE_TIME.NO_CACHE
    },
    method: 'POST',
    body: JSON.stringify(previewCart),
    signal
  });

  const data = (await response.json()) as {
    body: RawEPCCCartResponse;
    stripeCouponId?: string;
    requiresAuthorizationCharge?: boolean;
    codeRedeemed?: boolean;
    cancellationMinimumWaiverMonths?: number | null;
  };

  return {
    cart: data.body,
    stripeCouponId: data.stripeCouponId,
    requiresAuthorizationCharge: data.requiresAuthorizationCharge,
    codeRedeemed: data.codeRedeemed,
    cancellationMinimumWaiverMonths: data.cancellationMinimumWaiverMonths
  };
};

// New utility function in src/util/reactivate_client.ts
export const validatePromoCode = async (
  promoCode: string,
  subscriptionProductId: string,
  insuranceProductId: string
): Promise<{
  valid: boolean;
  promotion?: RawEPCCPromotion;
  error?: string;
}> => {
  const { cart: previewCart } = await createPreviewSubscriptionCart({
    subscriptionProductIds: [subscriptionProductId],
    insuranceProductId: insuranceProductId,
    promotionCodes: [promoCode]
  });

  const promotion = previewCart?.included?.promotions?.[0];

  return {
    valid: !!promotion,
    promotion: promotion,
    error: promotion ? undefined : 'Invalid or expired promo code'
  };
};

/**
 *
 * @param optIn boolean true if opt-in, false if opt-out
 * @param source where the opt-in happened (e.g. 'signup', 'cart'), recorded in Braze
 * @returns boolean indicating whether desired operation was successful
 */
export const setSMSOptIn = async (
  optIn: boolean,
  source?: string
): Promise<boolean> => {
  try {
    const response = await fetch('/api/user/opt-in/sms', {
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      },
      method: optIn ? 'POST' : 'DELETE',
      ...(optIn && source && { body: JSON.stringify({ source }) })
    });

    const result = await response.json();
    return result.success;
  } catch (e) {
    console.error('setSMSOptIn() error:', e);
    return false;
  }
};

export const getOldSubscriptionPlans = async (): Promise<
  { id: string; name: string; slug: string }[] | []
> => {
  try {
    const response = await fetch(`/api/user/membership`, {
      next: {
        revalidate: CACHE_TIME.NO_CACHE
      },
      method: 'GET'
    });
    const userMembership = await response.json();
    return userMembership;
  } catch (error) {
    return [];
  }
};

/**
 * Returns the most appropriate shipping address from a list of addresses.
 * Priority order (each sorted by most recently updated):
 * 1. Default shipping address (isDefault && !isBilling)
 * 2. Any non-billing address
 * 3. Default billing address (isDefault && isBilling)
 * 4. Any address
 *
 * @param addresses Array of EPCC addresses
 * @returns The most appropriate address, or undefined if no addresses exist
 */
export function getMostRecentShippingAddress(
  addresses: EPCCAddress[] | undefined
): EPCCAddress | undefined {
  if (!addresses?.length) {
    return undefined;
  }

  // Sort by updated_at descending (most recently updated first)
  const sortByUpdatedDesc = (a: EPCCAddress, b: EPCCAddress) => {
    const dateA = new Date(a.meta?.timestamps?.updated_at || 0).getTime();
    const dateB = new Date(b.meta?.timestamps?.updated_at || 0).getTime();
    return dateB - dateA;
  };

  // 1. Default shipping address (isDefault && !isBilling)
  const defaultShippingAddresses = addresses
    .filter((address) => address.isDefault && !address.isBilling)
    .sort(sortByUpdatedDesc);

  if (defaultShippingAddresses.length) {
    return defaultShippingAddresses[0];
  }

  // 2. Any non-billing address
  const nonBillingAddresses = addresses
    .filter((address) => !address.isBilling)
    .sort(sortByUpdatedDesc);

  if (nonBillingAddresses.length) {
    return nonBillingAddresses[0];
  }

  // 3. Default billing address (isDefault && isBilling)
  const defaultBillingAddresses = addresses
    .filter((address) => address.isDefault && address.isBilling)
    .sort(sortByUpdatedDesc);

  if (defaultBillingAddresses.length) {
    return defaultBillingAddresses[0];
  }

  // 4. Any address
  return [...addresses].sort(sortByUpdatedDesc)[0];
}

// Returns true if the user's priveeStatus is one of the given statuses (case-insensitive)
export function userHasPriveeStatus(
  user: User | undefined,
  status: PRIVEE_STATUS | PRIVEE_STATUS[]
): boolean {
  // Treat null/undefined priveeStatus the same as NONE
  const userPriveeStatus = user?.priveeStatus ?? PRIVEE_STATUS.NONE;

  const statusesToCheckAgainst = Array.isArray(status) ? status : [status];
  return statusesToCheckAgainst.some(
    (s) => userPriveeStatus.toLowerCase() === s.toLowerCase()
  );
}

// Semantic helpers that encode business rules regarding privee status in one place so consumers don't need to care
export const isPriveeActive = (user?: User) =>
  userHasPriveeStatus(user, PRIVEE_STATUS.ACTIVE);

export const isPriveeFrozen = (user?: User) =>
  userHasPriveeStatus(user, PRIVEE_STATUS.FROZEN);

export const isPriveeMember = (user?: User) =>
  userHasPriveeStatus(user, [PRIVEE_STATUS.ACTIVE, PRIVEE_STATUS.FROZEN]);

export const isPriveeInvited = (user?: User) =>
  userHasPriveeStatus(user, [
    PRIVEE_STATUS.INVITED,
    PRIVEE_STATUS.INITIATION_FAILED
  ]);

export const isPriveePending = (user?: User) =>
  userHasPriveeStatus(user, PRIVEE_STATUS.PENDING);

// Single source of truth for where a user belongs when entering the Réservé
// experience — used by /privee (to redirect non-invited users away) and by
// the Header / MobileMenu Réservé icon. Logged-out and invited users see the
// marketing/invitation page; everyone else goes to the collection page.
export const priveeLandingPath = (
  user?: User
): '/reserve' | '/collection/reserve' =>
  !user || isPriveeInvited(user) ? '/reserve' : '/collection/reserve';
